import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  notification,
  Row,
  Select,
  Checkbox,
  Card,
  TimePicker,
} from "antd";
import { Link } from "react-router-dom";
import TextArea from "antd/lib/input/TextArea";
import Modal from "antd/lib/modal/Modal";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { coreApi } from "../../api/calls";
import ProfileFormActionSpace from "../../components/ProfileFormActionSpace";

const FIELD_LIMITS = {
  city: 150,
  cleaning_room: 2000,
  cleaning_supplies: 2000,
  district: 150,
  postal_code: 40,
  notes: 2000,
  number_of_keys: 2000,
  requested_day_time_preference_note: 2000,
  scheduled_meeting_note: 2000,
  specific_request_note: 2000,
  water_access: 2000,
  ending_description: 2000,
};

/**
 * Building details
 * @component
 * @alias BuildingDetails
 * @property {object} building - contains building details
 * @returns Structured content of the building in <Row(s) /> and <Col(s) />
 */
const BuildingDetails = ({ building, setBuilding }) => {
  // Translations
  const { t } = useTranslation();
  const days = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];

  // States
  const [endTags, setEndTags] = useState([]);
  const [isFormReadOnly, setFormReadOnly] = useState(true);
  const [isFormSending, setFormSending] = useState(false);
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [selectedDays, setSelectedDays] = useState(() =>
    building?.requested_day ? JSON.parse(building.requested_day) : []
  );

  const [timeRanges, setTimeRanges] = useState(() => {
    try {
      const raw = JSON.parse(building?.requested_time || '[]');

      // 1. Legacy format: flat array with 2 ISO strings
      if (Array.isArray(raw) && raw.length === 2 && typeof raw[0] === 'string' && typeof raw[1] === 'string') {
        const fallbackDay = (building?.requested_day ? JSON.parse(building.requested_day) : [])[0] ?? "MO";
        return {
          [fallbackDay]: [[
            moment(raw[0], moment.ISO_8601, true).isValid() ? moment(raw[0]) : moment(raw[0], 'HH:mm'),
            moment(raw[1], moment.ISO_8601, true).isValid() ? moment(raw[1]) : moment(raw[1], 'HH:mm'),
          ]]
        };
      }

      // 2. New format
      const result = {};

      raw.forEach((entry) => {
        const [day, ranges] = Object.entries(entry)[0];

        if (!Array.isArray(ranges) || typeof day !== 'string') return;

        const validRanges = ranges
          .filter(([start, end]) => start && end) // remove null ranges
          .map(([start, end]) => [
            moment(start, moment.ISO_8601, true).isValid()
              ? moment(start)
              : moment(start, 'HH:mm'),
            moment(end, moment.ISO_8601, true).isValid()
              ? moment(end)
              : moment(end, 'HH:mm'),
          ]);

        if (validRanges.length > 0) {
          result[day] = validRanges;
        }
      });

      return result;
    } catch (error) {
      console.warn("Invalid requested_time format", error);
      return {};
    }
  });

  // Forms
  const [cancelBuildingForm] = Form.useForm();
  const [buildingForm] = Form.useForm();

  // Row gutter
  const gutter = [8, 0];

  const TimeRangesForDay = ({ day, ranges, onChange, isDisabled }) => (
    <div style={{ marginBottom: 12 }}>
      <Row align="middle" style={{ marginBottom: 6 }}>
        <Col span={24} style={{ fontWeight: 'bold', fontSize: 14 }}>
          {day}
        </Col>
      </Row>

      {ranges.map((range, idx) => (
        <Row key={idx} align="middle" gutter={8} style={{ marginBottom: 6 }}>
          <Col>
            <TimePicker.RangePicker
              value={range}
              onChange={(val) => {
                const updated = [...ranges];
                updated[idx] = val;
                onChange(updated);
              }}
              format="HH:mm"
              size="small"
              disabled={isDisabled}
            />
          </Col>
          {!isDisabled && (
            <Col>
              <Button
                danger
                size="small"
                shape="circle"
                icon="−"
                onClick={() =>
                  onChange(ranges.filter((_, i) => i !== idx))
                }
              />
            </Col>
          )}
        </Row>
      ))}

      {!isDisabled && (
        <Button
          type="link"
          size="small"
          onClick={() => onChange([...ranges, [null, null]])}
          style={{ paddingLeft: 0 }}
        >
          + Add Time Range
        </Button>
      )}
    </div>
  );

  useEffect(() => {
    let unmounted = false;

    coreApi
      .get("buildings/end-tags")
      .then((res) => {
        if (!unmounted) setEndTags(() => [...res.data]);
      })
      .catch((err) => {
        notification.error({ message: err.response.data.message });
      });

    return () => {
      unmounted = true;
    };
  }, [building]);

  useEffect(() => {
    buildingForm.setFieldsValue({
      requested_day: selectedDays,
    });
  }, []);

  const finishAction = (values) => {
    setFormSending(true);
    coreApi
      .put("/buildings/" + values.building_id, {
        ...values,
        ending_date: values.ending_date?.format("YYYY-MM-DD") || null,
        requested_day: selectedDays,
        requested_time:
          Object.entries(timeRanges).map(([day, ranges]) => ({
            [day]: ranges.map(([start, end]) => [start?.format('HH:mm'), end?.format('HH:mm')]),
          }))
      })
      .then((response) => {
        setFormReadOnly(true);
        notification.success({
          message: response.data.message,
        });
      })
      .catch((error) => {
        notification.error({ message: error.response.data.message });
      })
      .finally(() => {
        setFormSending(false);
      });
  };

  const handleCancelBuildingClick = () => {
    setIsCancelModalVisible(true);
    coreApi
      .get(`/buildings/${building?.id}/unpaid-invoices`)
      .then((response) => {
        setUnpaidInvoices(response.data);
        setIsConfirmed(response.data.length === 0);
      })
      .catch((error) => {
        notification.error({ message: error.response.data.message });
      });
  };

  return (
    <React.Fragment key={building?.id}>
      <Form form={buildingForm} name="building" onFinish={(values) => finishAction(values)}>
        <Row align="middle" gutter={gutter}>
          <Col>
            <Form.Item hidden name="building_id" initialValue={building?.id}>
              <Input readOnly={isFormReadOnly} bordered={!isFormReadOnly}></Input>
            </Form.Item>
          </Col>
          <Col>
            <Form.Item hidden name="client_id" initialValue={building?.client_id}>
              <Input readOnly={isFormReadOnly} bordered={!isFormReadOnly}></Input>
            </Form.Item>
          </Col>
          <Col>
            <Form.Item hidden name="city_branch_id" initialValue={building?.city_branch_id}>
              <Input readOnly={isFormReadOnly} bordered={!isFormReadOnly}></Input>
            </Form.Item>
          </Col>
          <Col span={24}>
            <div style={{ float: "right", display: "flex", gap: "12px", marginBottom: "16px" }}>
              {!isFormReadOnly && !building?.ending_date && (
                <React.Fragment>
                  <Modal
                    key={building?.id}
                    title={t("buildings.cancelModalTitle")}
                    open={isCancelModalVisible}
                    okType="danger"
                    okText={t("buildings.cancelBuilding")}
                    cancelText={t("common.cancel")}
                    onOk={() => {
                      cancelBuildingForm.submit();
                    }}
                    onCancel={() => {
                      setIsCancelModalVisible(false);
                    }}
                    okButtonProps={{ disabled: !isConfirmed }}
                  >
                    <Form
                      form={cancelBuildingForm}
                      name="building"
                      onFinish={(values) => {
                        coreApi
                          .put(`/buildings/${building?.id}/cancel`, {
                            ...values,
                            ending_date: values.ending_date?.format("YYYY-MM-DD"),
                          })
                          .then((response) => {
                            cancelBuildingForm.resetFields();

                            setIsCancelModalVisible(false);
                            setFormReadOnly(true);
                            setBuilding({ ...building, ...response.data.building });
                            buildingForm.resetFields();

                            notification.success({
                              message: response.data.message,
                            });
                          })
                          .catch((error) => {
                            console.log(error);
                          })
                          .finally(() => {
                            setFormSending(false);
                          });
                      }}
                    >
                      <Row gutter={gutter}>
                        <Col span={24}>
                          <Form.Item
                            name="ending_date"
                            label={t("buildings.endingDate")}
                            initialValue={building?.ending_date ? moment(building?.ending_date) : null}
                          >
                            <DatePicker></DatePicker>
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item
                            name="end_of_cooperation_tag_id"
                            label={t("buildings.endingTag")}
                            initialValue={building?.end_of_cooperation_tag_id}
                          >
                            <Select>
                              {endTags?.map((tag) => (
                                <Select.Option
                                  value={tag.end_of_cooperation_tag_id}
                                  key={tag.end_of_cooperation_tag_id}
                                >
                                  {tag.tag_name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item
                            name="ending_description"
                            label={t("buildings.endingDescription")}
                            initialValue={building?.ending_description}
                          >
                            <TextArea></TextArea>
                          </Form.Item>
                        </Col>
                      </Row>
                    </Form>
                    {unpaidInvoices.length > 0 ? (
                      <>
                        <div style={{ color: "red", marginBottom: "8px" }}>
                          {t("buildings.unpaidInvoiceMessage")}
                          <Checkbox
                            style={{ marginLeft: "10px" }}
                            checked={isConfirmed}
                            onChange={(e) => setIsConfirmed(e.target.checked)}
                          ></Checkbox>
                        </div>
                        <div style={{ overflowX: "auto", whiteSpace: "nowrap", padding: "10px 0" }}>
                          {unpaidInvoices.map((invoice) => (
                            <Link key={invoice.id} to={"/invoices/" + invoice.id}>
                              {invoice.invoice_number}
                            </Link>
                          )).reduce((prev, curr) => [prev, ", ", curr])}
                        </div>
                      </>
                    ) : (
                      <div style={{ marginBottom: "8px" }}>
                        {t("buildings.allPaidMessage")}
                        <Checkbox style={{ marginLeft: "10px" }} checked={isConfirmed}></Checkbox>
                      </div>
                    )}
                  </Modal>

                  <Button danger onClick={handleCancelBuildingClick}>
                    {t("buildings.cancelBuilding")}
                  </Button>
                </React.Fragment>
              )}

              <ProfileFormActionSpace
                formInstance={buildingForm}
                isFormReadOnly={isFormReadOnly}
                isFormSending={isFormSending}
                setFormEditable={() => setFormReadOnly(false)}
                setFormDisabled={() => setFormReadOnly(true)}
              />
            </div>
          </Col>
        </Row>

        {/* Adresa */}
        <Card
          size="small"
          title={t("common.address")}
          headStyle={{ backgroundColor: "#f0f0f0" }}
          style={{ marginBottom: 24 }}
          className="table-striped building-details"
        >
          <Row align="middle" gutter={gutter}>
            <Col span={4} style={{ fontWeight: "bold" }}>
              {t("buildings.city")}:
            </Col>
            <Col span={10}>
              <Form.Item name="city" initialValue={building?.city}>
                <Input
                  readOnly={isFormReadOnly}
                  bordered={!isFormReadOnly}
                  maxLength={FIELD_LIMITS.city}
                  showCount={!isFormReadOnly}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row align="middle" gutter={gutter}>
            <Col span={4} style={{ fontWeight: "bold" }}>
              {t("buildings.district")}:
            </Col>
            <Col span={10}>
              <Form.Item name="district" initialValue={building?.district}>
                <Input
                  readOnly={isFormReadOnly}
                  bordered={!isFormReadOnly}
                  maxLength={FIELD_LIMITS.district}
                  showCount={!isFormReadOnly}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row align="middle" gutter={gutter}>
            <Col span={4} style={{ fontWeight: "bold" }}>
              {t("buildings.postalCode")}:
            </Col>
            <Col span={10}>
              <Form.Item name="postal_code" initialValue={building?.postal_code}>
                <Input
                  readOnly={isFormReadOnly}
                  bordered={!isFormReadOnly}
                  maxLength={FIELD_LIMITS.postal_code}
                  showCount={!isFormReadOnly}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>
        {/* Poznámky a speciální požadavky */}
        <Card
          size="small"
          title={t("buildings.notes")}
          headStyle={{ backgroundColor: "#f0f0f0" }}
          style={{ marginBottom: 24 }}
          className="table-striped building-details"
        >
          <Row align="middle" gutter={gutter}>
            {/* Úklidová místnost */}
            <Col span={4} style={{ fontWeight: "bold" }}>
              {t("buildings.cleaningRoomDescription")}:
            </Col>
            <Col span={10}>
              <Form.Item
                name="cleaning_room"
                initialValue={building?.cleaning_room}
              >
                <TextArea
                  readOnly={isFormReadOnly}
                  bordered={!isFormReadOnly}
                  maxLength={FIELD_LIMITS.cleaning_room}
                  showCount={!isFormReadOnly}
                  autoSize={{ minRows: 1, maxRows: 6 }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row align="middle" gutter={gutter}>
            {/* Přístup k vodě */}
            <Col span={4} style={{ fontWeight: "bold" }}>
              {t("buildings.waterAccess")}:
            </Col>
            <Col span={10}>
              <Form.Item
                name="water_access"
                initialValue={building?.water_access}
              >
                <TextArea
                  readOnly={isFormReadOnly}
                  bordered={!isFormReadOnly}
                  maxLength={FIELD_LIMITS.water_access}
                  showCount={!isFormReadOnly}
                  autoSize={{ minRows: 1, maxRows: 6 }} />
              </Form.Item>
            </Col>
          </Row>
          <Row align="middle" gutter={gutter}>
            {/* Pomůcky na úklid */}
            <Col span={4} style={{ fontWeight: "bold" }}>
              {t("buildings.cleaningSupplies")}:
            </Col>
            <Col span={10}>
              <Form.Item name="cleaning_supplies" initialValue={building?.cleaning_supplies}>
                <TextArea
                  readOnly={isFormReadOnly}
                  bordered={!isFormReadOnly}
                  maxLength={FIELD_LIMITS.cleaning_supplies}
                  showCount={!isFormReadOnly}
                  autoSize={{ minRows: 1, maxRows: 6 }} />
              </Form.Item>
            </Col>
          </Row>
          <Row align="middle" gutter={gutter}>
            {/* Klíče */}
            <Col span={4} style={{ fontWeight: "bold" }}>
              {t("buildings.keys")}:
            </Col>
            <Col span={10}>
              <Form.Item
                name="number_of_keys"
                initialValue={building?.number_of_keys}
              >
                <TextArea
                  readOnly={isFormReadOnly}
                  bordered={!isFormReadOnly}
                  maxLength={FIELD_LIMITS.number_of_keys}
                  showCount={!isFormReadOnly}
                  autoSize={{ minRows: 1, maxRows: 6 }} />
              </Form.Item>
            </Col>
          </Row>
          <Row align="middle" gutter={gutter}>
            {/* Specifické požadavky */}
            <Col span={4} style={{ fontWeight: "bold" }}>
              {t("buildings.specificRequirements")}:
            </Col>
            <Col span={10}>
              <Form.Item name="specific_request_note" initialValue={building?.specific_request_note}>
                <TextArea
                  readOnly={isFormReadOnly}
                  bordered={!isFormReadOnly}
                  maxLength={FIELD_LIMITS.specific_request_note}
                  showCount={!isFormReadOnly}
                  autoSize={{ minRows: 1, maxRows: 6 }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row align="middle" gutter={gutter}>
            {/* Poznámky k budově pro Andulku */}
            <Col span={4} style={{ fontWeight: "bold" }}>
              {t("buildings.buildingNotesProAna")}:
            </Col>
            <Col span={10}>
              <Form.Item name="notes" initialValue={building?.notes}>
                <TextArea
                  readOnly={isFormReadOnly}
                  bordered={!isFormReadOnly}
                  maxLength={FIELD_LIMITS.notes}
                  showCount={!isFormReadOnly}
                  autoSize={{ minRows: 1, maxRows: 6 }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row align="middle" gutter={gutter}>
            {/* Požadovaný den */}
            <Col span={4} style={{ fontWeight: "bold" }}>
              {t("buildings.requestedDay")}:
            </Col>
            <Col span={10}>
              <Form.Item name="requested_day" initialValue={selectedDays}>
                <Select
                  mode="multiple"
                  onChange={(days) => {
                    setSelectedDays(days);
                    const updated = {};
                    days.forEach(day => {
                      updated[day] = timeRanges[day] || [[null, null]];
                    });
                    setTimeRanges(updated);
                  }}
                  disabled={isFormReadOnly}
                >
                  {days.map((day) => (
                    <Select.Option key={day} value={day}>
                      {t("rrules.byweekday." + day)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row align="middle" gutter={gutter}>
            {/* Požadovaný čas */}
            <Col span={4} style={{ fontWeight: "bold" }}>
              {t("buildings.requestedTime")}:
            </Col>
            <Col span={10}>
              <Form.Item name="requested_time">
                {selectedDays.map((day) => (
                  <TimeRangesForDay
                    key={day}
                    day={t("rrules.byweekday." + day)}
                    ranges={
                      Array.isArray(timeRanges[day]) && timeRanges[day].length
                        ? timeRanges[day]
                        : [[null, null]]
                    }
                    onChange={(val) =>
                      setTimeRanges((prev) => ({
                        ...prev,
                        [day]: val,
                      }))
                    }
                    isDisabled={isFormReadOnly}
                  />
                ))}
              </Form.Item>
            </Col>
          </Row>
          <Row align="middle" gutter={gutter}>
            {/* Preference času */}
            <Col span={4} style={{ fontWeight: "bold" }}>
              {t("buildings.dayTimePreferenceNote")}:
            </Col>
            <Col span={8}>
              <Form.Item name="requested_day_time_preference_note"
                         initialValue={building?.requested_day_time_preference_note}>
                <TextArea
                  readOnly={isFormReadOnly}
                  bordered={!isFormReadOnly}
                  maxLength={FIELD_LIMITS.requested_day_time_preference_note}
                  showCount={!isFormReadOnly}
                  autoSize={{ minRows: 1, maxRows: 6 }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Schůze */}
        <Card
          size="small"
          title={t("buildings.meeting")}
          headStyle={{ backgroundColor: "#f0f0f0" }}
          style={{ marginBottom: 24 }}
          className="table-striped building-details"
        >
          <Row align="middle" gutter={gutter}>
            {/* Termín plánované schůze */}
            <Col span={4} style={{ fontWeight: "bold" }}>
              {t("buildings.scheduledMeetingDate")}:
            </Col>
            <Col span={4}>
              <Form.Item
                name="scheduled_meeting_date"
                initialValue={building?.scheduled_meeting_date ? moment(building?.scheduled_meeting_date) : null}
              >
                <DatePicker disabled={isFormReadOnly} bordered={!isFormReadOnly} />
              </Form.Item>
            </Col>
            {/* Druhý možný termín plánované schůze */}
            <Col span={4} style={{ fontWeight: "bold" }}>
              {t("buildings.scheduledMeetingDateAlternate")}:
            </Col>
            <Col span={4}>
              <Form.Item name="scheduled_meeting_date_alternate" initialValue={building?.scheduled_meeting_date_alternate ? moment(building?.scheduled_meeting_date_alternate) : null}>
                <DatePicker disabled={isFormReadOnly} bordered={!isFormReadOnly} />
              </Form.Item>
            </Col>
          </Row>
          <Row align="middle" gutter={gutter}>
            {/* Termín plánované schůze poznámka */}
            <Col span={4} style={{ fontWeight: "bold" }}>
              {t("buildings.scheduledMeetingNote")}:
            </Col>
            <Col span={10}>
              <Form.Item name="scheduled_meeting_note" initialValue={building?.scheduled_meeting_note}>
                <TextArea
                  readOnly={isFormReadOnly}
                  bordered={!isFormReadOnly}
                  maxLength={FIELD_LIMITS.scheduled_meeting_note}
                  showCount={!isFormReadOnly}
                  autoSize={{ minRows: 1, maxRows: 6 }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Ukončení objednávky */}
        <Card
          size="small"
          title={t("buildings.orderTermination")}
          headStyle={{ backgroundColor: "#f0f0f0" }}
          style={{ marginBottom: 24 }}
          className="table-striped building-details"
        >
          <Row align="middle" gutter={gutter}>
            <Col span={4} style={{ fontWeight: "bold" }}>
              {t("buildings.endingDate")}:
            </Col>
            <Col span={10}>
              <Form.Item
                name="ending_date"
                initialValue={building?.ending_date ? moment(building?.ending_date) : null}
              >
                <DatePicker disabled={isFormReadOnly} bordered={!isFormReadOnly} />
              </Form.Item>
            </Col>
          </Row>
          <Row align="middle" gutter={gutter}>
            <Col span={4} style={{ fontWeight: "bold" }}>
              {t("buildings.endingTag")}:
            </Col>
            <Col span={10}>
              <Form.Item
                name="end_of_cooperation_tag_id"
                initialValue={building?.end_of_cooperation_tag_id}
              >
                <Select disabled={isFormReadOnly} bordered={!isFormReadOnly}>
                  {endTags?.map((tag) => (
                    <Select.Option value={tag.end_of_cooperation_tag_id} key={tag.end_of_cooperation_tag_id}>
                      {tag.tag_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row align="middle" gutter={gutter}>
            <Col span={4} style={{ fontWeight: "bold" }}>
              {t("buildings.endingDescription")}:
            </Col>
            <Col span={10}>
              <Form.Item
                name="ending_description"
                initialValue={building?.ending_description}
              >
                <TextArea
                  readOnly={isFormReadOnly}
                  bordered={!isFormReadOnly}
                  maxLength={FIELD_LIMITS.ending_description}
                  showCount={!isFormReadOnly}
                  autoSize={{ minRows: 1, maxRows: 6 }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>
    </React.Fragment>
  );
};

export default BuildingDetails;
